import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { AxiosError, AxiosResponse } from "axios";
import { useChangeSelectedCompanyStore } from "@/stores/useChangeSelectedCompanyStore";
import { useCompanyListStore } from "@/stores/useCompanyListStore";
import { useDashboardStore } from "@/stores/useDashboardStore";
import { useDetailsStore } from "@/stores/useDetailsStore";
import {
  resetAllTableStoreCurrentPage,
  resetAllTableStoreDetails,
  useTableStoreByRoute
} from "@/stores/useTableStore";
import { useTokenRefreshLoadingStore } from "@/stores/useTokenRefreshLoadingStore";
import { useTokenStore } from "@/stores/useTokenStore";

import useAxiosPrivate from "../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../api/resources";
import { ALERT_STATUS } from "../../../../../context/alert/types";
import { useAuth } from "../../../../../context/Auth/AuthProvider";
import { CompaniesDto, ROUTES } from "../../../../../shared";
import { sortCompanies } from "../../../../../shared/functions/functions";
import useRefreshTableData from "../../../../../shared/hooks/useRefreshTableData";
import useResponse from "../../../../../shared/hooks/useResponse";
import { MIN_CHAR_LENGTH_IN_SEARCH } from "../../../../Search/constants";
import { deselectAllRows } from "../../../../Table/functions";
import { generateErrorMessageToDisplay } from "../functions";
import useChangeCompanyFunctions from "./useChangeCompanyFunctions";
import useCompanies from "./useCompanies";

const useChangeSelectedCompany = () => {
  const { tokenInfo } = useAuth();
  const { handleResponse } = useResponse();
  const { postData, getData } = useAxiosPrivate();
  const { refreshTableData } = useRefreshTableData();
  const { refreshPageData } = useChangeCompanyFunctions();

  const { getQueryData } = useCompanies();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  const {
    params: { contains },
    setLoading: setIsTableLoading,
    setSelectedRows
  } = useTableStoreByRoute(location.pathname as ROUTES);

  const setIsDashboardLoading = useDashboardStore(
    state => state.setIsDashboardLoading
  );

  const isTokenRefreshLoading = useTokenRefreshLoadingStore(
    state => state.isTokenRefreshLoading
  );

  const setIsTokenRefreshLoading = useTokenRefreshLoadingStore(
    state => state.setIsTokenRefreshLoading
  );

  const setCompanyList = useCompanyListStore(state => state.setCompanyList);

  const setIsChangeSelectedCompany = useChangeSelectedCompanyStore(
    state => state.setIsChangeSelectedCompany
  );

  const setResetDetailsSection = useDetailsStore(
    state => state.setResetDetailsSection
  );

  const [isRefreshTableEnabled, setIsRefreshTableEnabled] =
    useState<boolean>(false);

  const resource = ApiResources.ChangeSelectedCompany;

  useEffect(() => {
    if (isRefreshTableEnabled) {
      refreshPageData(refreshTable);
      // add dashboard indicator to reload data in a correct order
      location.pathname === ROUTES.Dashboard &&
        setIsChangeSelectedCompany(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenInfo, isRefreshTableEnabled, location]);

  const refreshTable = () => {
    callApiToChangeCompany();

    resetAllTableStoreCurrentPage();
    // deselect all table rows
    deselectAllRows(setSelectedRows);
  };

  const callApiToChangeCompany = () => {
    const searchParams = contains ?? "";

    const queryWithSearch =
      searchParams?.length >= MIN_CHAR_LENGTH_IN_SEARCH && searchParams;

    const searchQuery = queryWithSearch || null;

    searchQuery && refreshTableData(false, searchQuery, true);
    !searchQuery && refreshTableData(undefined, undefined, true);
  };

  const setTokensAndEnableRefresh = (
    response: AxiosResponse<any, any>,
    setTokenInfo: any,
    shouldRefreshTable: boolean = true
  ) => {
    setTokenInfo({
      token: response.data.token,
      refreshToken: response.data.refreshToken
    });

    shouldRefreshTable && setIsRefreshTableEnabled(true);
  };

  const callDashboardLoading = (isLoading: boolean) => {
    if (location.pathname === ROUTES.Dashboard) {
      setIsDashboardLoading(isLoading);
    } else {
      setIsTableLoading(isLoading);
    }
  };

  const changeSelectedCompany = async (
    selectedCompany: CompaniesDto | string,
    shouldRefreshTable?: boolean,
    navigateToRoute?: ROUTES,
    shouldQueryData?: boolean
  ) => {
    const currentTokenInfo = useTokenStore.getState().tokenInfo;
    const setTokenInfo = useTokenStore.getState().setTokenInfo;

    const payload = {
      accessToken: currentTokenInfo?.token,
      refreshToken: currentTokenInfo?.refreshToken,
      companyId: selectedCompany
    };

    if (!isTokenRefreshLoading) {
      setIsTokenRefreshLoading(true);
      callDashboardLoading(true);
      try {
        if (shouldQueryData) {
          await getQueryData(undefined, selectedCompany as string);
        }

        const response = await postData(resource, payload);
        response &&
          setTokensAndEnableRefresh(response, setTokenInfo, shouldRefreshTable);
      } catch (error) {
        const err = error as AxiosError<any>;
        handleResponse(
          ALERT_STATUS.Critical,
          generateErrorMessageToDisplay(err, t)
        );
        callDashboardLoading(false);
      } finally {
        setIsTokenRefreshLoading(false);
        navigateToRoute && navigate(navigateToRoute);
      }
    }
  };

  // close details section in all the pages
  const handleCloseDesktopDetails = () => {
    resetAllTableStoreDetails();
    setResetDetailsSection(false);
  };

  const getCompanyList = async () => {
    try {
      const response = await getData(ApiResources.CompanyList);

      const sortedCompanies = sortCompanies(response.data.companies, true);

      response && setCompanyList(sortedCompanies);

      return;
    } catch (error) {
      const err = error as AxiosError;
      if (err) {
        handleResponse(ALERT_STATUS.Critical, err.message);
      }
    }
  };

  return {
    changeSelectedCompany,
    handleCloseDesktopDetails,
    getCompanyList
  };
};

export default useChangeSelectedCompany;
